import { IBorderRadiusData } from './data/style-property-data.model';

export type FieldType =
    | 'text'
    | 'email'
    | 'firstname'
    | 'lastname'
    | 'companyname'
    | 'birthday'
    | 'signature'
    | 'subscribe'
    | 'number'
    | 'tel'
    | 'url'
    | 'date'
    | 'textarea'
    | 'file'
    | 'password'
    | 'hidden'
    | 'checkbox'
    | 'radio'
    | 'dropdown'
    | 'captcha'
    | 'gdpr';

export type WidgetType =
    | 'submit'
    | 'text'
    | 'header'
    | 'divider'
    | 'image'
    | 'spacer'
    | 'signin'
    | 'signup'
    | 'forgotpassword'
    | 'navbutton';

export type GridType =
    | 'section'
    | 'mesh'
    | 'row'
    | 'column';

export type SectionType =
    | 'header'
    | 'form'
    | 'footer'
    | 'common'
    | 'submit';

export interface ICustomCssClasses {
    cssClasses?: string[];
}

export interface BaseFieldJsonData extends ICustomCssClasses {
    id: string;
    type: FieldType;
    label?: string;
    showDescription?: boolean;
    description?: string;
    required?: boolean;
    showPlaceholder?: boolean;
    placeholder?: string;
    hiddenLabel?: boolean;
    crmFieldData?: CrmFieldJsonData | null;
    isSystem?: boolean;
}

export interface CrmFieldJsonData {
    mixedType: 'CustomField' | 'ContactField';
    type: string;
    name: string;
    id?: string;
    systemName?: string;
}

export interface BaseWidgetJsonData extends ICustomCssClasses {
    id: string;
    type: WidgetType;
}

export interface BaseGridJsonData {
    id: string;
    type: GridType;
}

export interface TextFieldJsonData extends BaseFieldJsonData {
    type: 'text';
}

export interface TextareaFieldJsonData extends BaseFieldJsonData {
    type: 'textarea';
    height?: number;
}

export interface NumberFieldJsonData extends BaseFieldJsonData {
    type: 'number';
}

export interface EmailFieldJsonData extends BaseFieldJsonData {
    type: 'email';
}

export interface FirstNameFieldJsonData extends BaseFieldJsonData {
    type: 'firstname';
}

export interface LastNameFieldJsonData extends BaseFieldJsonData {
    type: 'lastname';
}

export interface CompanyNameFieldJsonData extends BaseFieldJsonData {
    type: 'companyname';
}

export interface BirthdayFieldJsonData extends BaseFieldJsonData {
    type: 'birthday';
    format: string;
}

export interface SignatureFieldJsonData extends BaseFieldJsonData {
    type: 'signature';
    clearButton: string;
    height?: number;
}

export interface SubscribeFieldJsonData extends BaseFieldJsonData {
    type: 'subscribe';
    defaultValue?: boolean;
}

export interface TelFieldJsonData extends BaseFieldJsonData {
    type: 'tel';
}

export interface DateFieldJsonData extends BaseFieldJsonData {
    type: 'date';
    format: string;
    fillCurrentDate?: boolean;
}

export interface CheckboxFieldJsonData extends BaseFieldJsonData {
    type: 'checkbox';
    options: FieldOption[];
    minCount?: string;
    maxCount?: string;
}

export interface FieldOption {
    value: string;
    label: string;
}

export interface RadioFieldJsonData extends BaseFieldJsonData {
    type: 'radio';
    options: FieldOption[];
}

export interface DropdownFieldJsonData extends BaseFieldJsonData {
    type: 'dropdown';
    options: FieldOption[];
}

export interface FileFieldJsonData extends BaseFieldJsonData {
    type: 'file';
    fileSelectText?: string;
}

export interface HiddenFieldJsonData extends BaseFieldJsonData {
    type: 'hidden';
    value: string;
    dataType: 'text' | 'number' | 'date' | 'email';
}

export interface UrlFieldJsonData extends BaseFieldJsonData {
    type: 'url';
}

export interface PasswordConfirmationSettings {
    enabled: boolean;
    label?: string;
    placeholder?: string;
    description?: string;
    hiddenLabel?: boolean;
}

export interface PasswordFieldJsonData extends BaseFieldJsonData {
    type: 'password';
    showPasswordToggle?: boolean;
    showPasswordStrength?: boolean;
    confirmation?: PasswordConfirmationSettings;
}

export interface SubmitWidgetJsonData extends BaseWidgetJsonData {
    type: 'submit';
    variant?: 'simple' | 'extended';
    submitLabel?: string;
    previousLabel?: string;
    nextLabel?: string;
    height: string;
}

export interface TextWidgetJsonData extends BaseWidgetJsonData {
    type: 'text';
    content: string;
}

export interface HeaderWidgetJsonData extends BaseWidgetJsonData {
    type: 'header';
    content: string;
}

export interface DividerWidgetJsonData extends BaseWidgetJsonData {
    type: 'divider';
    style?: 'solid' | 'dashed' | 'dotted';
    thickness?: number;
    color?: string;
}

export interface IImageWidgetStyle {
    height?: string;
    heightFixed?: boolean;
    aspectRatio?: number;
    fitMode?: 'cover' | 'contain';
    imageAlignment?: 'start' | 'center' | 'end';
    borderRadius?: IBorderRadiusData | null;
    borderThickness?: number;
    borderColor?: string | null;
}

export interface ImageWidgetJsonData extends BaseWidgetJsonData {
    type: 'image';
    src?: string;
    alt?: string;
    style?: IImageWidgetStyle;
}

export interface SpacerWidgetJsonData extends BaseWidgetJsonData {
    type: 'spacer';
    height: string;
    shouldUseMaxHeight: boolean;
}

export interface SignInWidgetJsonData extends BaseWidgetJsonData {
    type: 'signin';
    content: string;
}

export interface SignUpWidgetJsonData extends BaseWidgetJsonData {
    type: 'signup';
    content: string;
}

export interface ForgotPasswordWidgetJsonData extends BaseWidgetJsonData {
    type: 'forgotpassword';
    content: string;
}

export interface NavButtonWidgetJsonData extends BaseWidgetJsonData {
    type: 'navbutton';
    direction?: 'previous' | 'next';
    label?: string;
    height: string;
}

export interface SectionJsonData extends BaseGridJsonData {
    type: 'section';
    sectionType: SectionType;
}

export interface MeshJsonData extends BaseGridJsonData {
    type: 'mesh';
}

export interface RowJsonData extends BaseGridJsonData {
    type: 'row';
}

export interface ColumnJsonData extends BaseGridJsonData {
    type: 'column';
    size: number;
}

export type FieldJsonData =
    | TextFieldJsonData
    | EmailFieldJsonData
    | FirstNameFieldJsonData
    | LastNameFieldJsonData
    | CompanyNameFieldJsonData
    | BirthdayFieldJsonData
    | SignatureFieldJsonData
    | SubscribeFieldJsonData
    | NumberFieldJsonData
    | TelFieldJsonData
    | UrlFieldJsonData
    | DateFieldJsonData
    | TextareaFieldJsonData
    | FileFieldJsonData
    | HiddenFieldJsonData
    | CheckboxFieldJsonData
    | RadioFieldJsonData
    | DropdownFieldJsonData
    | PasswordFieldJsonData;

export type WidgetJsonData =
    | SubmitWidgetJsonData
    | TextWidgetJsonData
    | HeaderWidgetJsonData
    | DividerWidgetJsonData
    | ImageWidgetJsonData
    | SpacerWidgetJsonData
    | SignInWidgetJsonData
    | SignUpWidgetJsonData
    | ForgotPasswordWidgetJsonData
    | NavButtonWidgetJsonData;

export type GridJsonData =
    | SectionJsonData
    | MeshJsonData
    | RowJsonData
    | ColumnJsonData;

export type ElementJsonData = FieldJsonData | WidgetJsonData;

export interface RenderOptions {
    runtimeContext?: any;
    language?: string;
}

export interface WidgetRenderOptions {
    runtimeContext?: any;
    baseUrl?: string;
    [key: string]: any;
}

export interface CaptchaRuntimeConfig {
    type: 'TruCaptcha' | 'ReCaptchaV2' | 'ReCaptchaV3';
    siteKey?: string;
    showBadge?: boolean;
    darkMode?: boolean;
}

export interface GdprRuntimeConfig {
    enabled: boolean;
    consentText: string;
    privacyPolicyUrl?: string;
}

export interface AccountRuntimeConfig {
    accountId: string;
}

export interface RuntimeContext {
    captcha?: CaptchaRuntimeConfig;
    gdpr?: GdprRuntimeConfig;
    account?: AccountRuntimeConfig;
    mode?: HtmlRenderMode;
}

export type HtmlRenderMode = 'editor' | 'view';

export type FieldGroup = 'textFields' | 'radioFields' | 'fileFields' | 'hiddenFields';

export function getFieldGroup(fieldType: FieldType): FieldGroup | undefined {
    switch (fieldType) {
        case 'text':
        case 'email':
        case 'firstname':
        case 'lastname':
        case 'companyname':
        case 'birthday':
        case 'number':
        case 'tel':
        case 'url':
        case 'date':
        case 'textarea':
        case 'password':
        case 'dropdown':
        case 'signature':
            return 'textFields';
        case 'radio':
            return 'radioFields';
        case 'file':
            return 'fileFields';
        case 'hidden':
            return 'hiddenFields';
        case 'subscribe':
        case 'checkbox':
        case 'captcha':
        case 'gdpr':
            return undefined;
    }
}
